import * as z from "zod/mini";
import { PaymentMethod, PaymentRegion } from "./paymentMethod";
import type { FormFieldConfig } from "../../../mixitone/src/components/Forms/FormFieldsDefinition";

/**
 * Configuration schema for NZ Bank payment method
 */
export const NzBankConfigSchema = z.object({
  amount: z.number(),
  accountNumber: z
    .string()
    .check(
      z.length(15, "Bank account number must be exactly 15 characters"),
      z.regex(/^\d{2}-\d{4}-\d{7}-\d{2}$/, "Bank account number must be in format XX-XXXX-XXXXXXX-XX"),
    ),
  accountName: z.string().check(z.minLength(1, "Bank account name is required")),
});

/**
 * Type for NZ Bank payment method configuration
 */
export type NzBankConfig = z.infer<typeof NzBankConfigSchema>;

/**
 * NZ Bank payment method implementation
 */
export class NzBank extends PaymentMethod<NzBankConfig> {
  id = "nz-bank";
  name = "Manual NZ bank transfer";
  supportedRegions: PaymentRegion[] = ["nz"];
  format = "XX-XXXX-XXXXXXX-XX";

  /**
   * Get the configuration schema for NZ Bank payments
   */
  getConfigSchema() {
    return NzBankConfigSchema;
  }

  /**
   * Get the form field configurations for NZ Bank payments
   */
  getConfigFields(): FormFieldConfig[] {
    return [
      {
        type: "number",
        name: "amount",
        label: "Amount",
        required: true,
        helpText: "The amount to be paid by manual bank transfer",
        prefix: "$",
        min: 0,
        step: 0.01,
      },
      {
        type: "segmented",
        name: "accountNumber",
        label: "Bank account number",
        required: true,
        format: this.format,
        validate: (value: string) => {
          if (!value) return "Bank account number is required";
          if (!/^\d{2}-\d{4}-\d{7}-\d{2}$/.test(value)) {
            return "Bank account number must be in format XX-XXXX-XXXXXXX-XX";
          }
        },
      },
      {
        type: "text",
        name: "accountName",
        label: "Account name",
        required: true,
        helpText: "The name of the bank account holder",
      },
    ];
  }

  /**
   * Get a short description of the NZ Bank payment method
   */
  getDescription(): string {
    return "Pay later using the club's NZ account details";
  }

  /**
   * Get instructions for how to make a bank transfer payment
   * @param config The bank account configuration
   */
  getInstructions(config: NzBankConfig): string {
    return `
Please make payment to the following bank account:

Account number: ${config.accountNumber}
Account name: ${config.accountName}

Please include your name and reference number in the payment details.
Your payment may take 1-2 business days to be processed.
    `.trim();
  }

  /**
   * Validate that the configuration is valid
   * @param config The configuration to validate
   */
  validateConfig(config: NzBankConfig): boolean {
    const result = NzBankConfigSchema.safeParse(config);
    return result.success;
  }
}

/**
 * Create a new NZ Bank payment method instance
 */
export const createNzBankPaymentMethod = (): PaymentMethod<NzBankConfig> => {
  return new NzBank();
};
